import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useGT } from 'gt-react';
import { ReportCanvasLayoutSchema } from '@erp/rpc';
import {
  useResetUserReportCanvasConfig,
  useUpsertUserReportCanvasConfig,
  useUserReportCanvasConfig,
} from '@/api/rpc/hooks/core/useUserReportCanvasConfig';
import { hydrateLayout } from '@/components/report-canvas/hydrate';
import {
  deserializeLayout,
  serializeLayout,
} from '@/components/report-canvas/serialization';
import type { ComponentRegistry } from '@/components/report-canvas/registry';
import type { GridLayout } from '@/components/report-canvas/types';

export type ReportCanvasConfigSource = 'user' | 'default';

/**
 * Parses a stored layout defensively: a document that fails validation
 * (corrupt JSON shape, oversized) is treated as absent so the effective
 * layout falls back to the developer default instead of crashing the page.
 * Valid documents are deserialized into the runtime shape on the Report grid
 * (fresh item ids; a layout saved as rows converts).
 */
function parseStoredLayout(
  stored: unknown,
  registry: ComponentRegistry,
): GridLayout | null {
  if (stored == null) return null;
  const result = ReportCanvasLayoutSchema.safeParse(stored);
  return result.success ? deserializeLayout(result.data, registry) : null;
}

/**
 * Layout resolution for a report-canvas surface: the user's personal
 * override ?? the developer default (which lives in code, never persisted).
 * Persisted layouts are hydrated against the registry (config version
 * migrations + defaults-fill) before use, so callers always see
 * current-shape configs.
 */
export function useReportCanvasConfig({
  surfaceId,
  developerDefault,
  registry,
}: {
  surfaceId: string;
  developerDefault: GridLayout;
  registry: ComponentRegistry;
}) {
  const gt = useGT();
  const userQuery = useUserReportCanvasConfig(surfaceId);
  const upsertUser = useUpsertUserReportCanvasConfig();
  const resetUser = useResetUserReportCanvasConfig();

  const isLoading = userQuery.isLoading;

  const { effectiveLayout, source, fallbackLayout } = useMemo((): {
    effectiveLayout: GridLayout;
    source: ReportCanvasConfigSource;
    /** What the surface shows without a user override. */
    fallbackLayout: GridLayout;
  } => {
    const fallback = hydrateLayout(developerDefault, registry);

    const userLayout = parseStoredLayout(userQuery.data?.layout, registry);
    if (userLayout !== null) {
      return {
        effectiveLayout: hydrateLayout(userLayout, registry),
        source: 'user',
        fallbackLayout: fallback,
      };
    }
    return {
      effectiveLayout: fallback,
      source: 'default',
      fallbackLayout: fallback,
    };
  }, [userQuery.data, developerDefault, registry]);

  // Serializes the draft to the persisted shape and validates it. Backstop
  // only: the DnD controller refuses drops that would exceed the schema's
  // size caps, so a saveable draft is the invariant. safeParse keeps an
  // unexpected violation from throwing out of a click handler.
  const validateForSave = useCallback(
    (layout: GridLayout) => {
      const result = ReportCanvasLayoutSchema.safeParse(
        serializeLayout(layout),
      );
      if (!result.success) {
        toast.error(
          gt('This layout cannot be saved. Please adjust it and try again.'),
        );
        return null;
      }
      return result.data;
    },
    [gt],
  );

  // Save/reset report success as a boolean so callers can keep the edit UI
  // (and the user's draft) open on failure. There is no app-wide mutation
  // error handler, so failures are toasted here.
  const notifySaveFailed = useCallback(() => {
    toast.error(gt('Failed to save the layout. Please try again.'));
  }, [gt]);

  const saveUserLayout = useCallback(
    async (layout: GridLayout): Promise<boolean> => {
      const validated = validateForSave(layout);
      if (!validated) return false;
      try {
        await upsertUser.mutateAsync({ surfaceId, layout: validated });
        return true;
      } catch {
        notifySaveFailed();
        return false;
      }
    },
    [upsertUser, surfaceId, validateForSave, notifySaveFailed],
  );

  const resetUserLayout = useCallback(async (): Promise<boolean> => {
    try {
      await resetUser.mutateAsync({ surfaceId });
      return true;
    } catch {
      notifySaveFailed();
      return false;
    }
  }, [resetUser, surfaceId, notifySaveFailed]);

  return {
    effectiveLayout,
    source,
    fallbackLayout,
    isLoading,
    isSaving: upsertUser.isPending || resetUser.isPending,
    saveUserLayout,
    resetUserLayout,
  };
}
